import { chapters } from "../content/journey";
import type { ChapterId, StoredProgress } from "../types";
import {
  chinaMapModel,
  commonStageModel,
  sceneModelCatalog,
  type LoadedSceneModelId,
  type SceneModelId
} from "./modelCatalog";

const chapterStageModels: Record<ChapterId, readonly LoadedSceneModelId[]> = {
  "chapter-1": ["common"],
  "chapter-2": ["phenology"],
  "chapter-3": ["latitude"],
  "chapter-4": ["common"]
};

const prefetchedGlbUrls = new Set<string>();
let natureStageBundlePromise: Promise<unknown> | null = null;

function preloadGlb(url: string): void {
  if (prefetchedGlbUrls.has(url)) return;
  prefetchedGlbUrls.add(url);
  void fetch(url).catch(() => {
    prefetchedGlbUrls.delete(url);
  });
}

export function resolveLikelyChapter(
  progress: Pick<StoredProgress, "completedChapters">
): ChapterId {
  const completed = new Set(progress.completedChapters);
  const next = chapters.find((chapter) => !completed.has(chapter.id));
  return next?.id ?? chapters[chapters.length - 1].id;
}

export function preloadStageModelFiles(modelId: LoadedSceneModelId): void {
  preloadGlb(commonStageModel.url);
  if (modelId === "common") return;
  preloadGlb(sceneModelCatalog[modelId as SceneModelId].url);
  if (modelId === "latitude" || modelId === "longitude") {
    preloadGlb(chinaMapModel.url);
  }
}

export function preloadChapterStageAssets(chapterId: ChapterId): void {
  for (const modelId of chapterStageModels[chapterId]) {
    preloadStageModelFiles(modelId);
  }
}

export function preloadNatureStageBundle(): Promise<unknown> {
  natureStageBundlePromise ??= import("./NatureStage");
  return natureStageBundlePromise;
}

function scheduleIdle(task: () => void): void {
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(() => task(), { timeout: 2500 });
    return;
  }
  window.setTimeout(task, 400);
}

/** Warm the 3D runtime chunk and the models for the chapter the learner is most likely to open next. */
export function warmStageForJourneyMap(progress: Pick<StoredProgress, "completedChapters">): void {
  scheduleIdle(() => {
    void preloadNatureStageBundle();
    preloadChapterStageAssets(resolveLikelyChapter(progress));
  });
}
